import React from 'react';
import { ProductFilter } from './ProductFilter';
import { Product } from 'core/product/Product';
import { ProductListColumns, useProductListModel } from './ProductListModel';
import { StickableBootstrapTable } from 'components/Table/StickableBootstrapTable';
import i18n from 'i18n';
import styles from './productList.module.scss';
import { Pagination } from 'core/pagination/Pagination';
import { TableChangeHandler } from 'react-bootstrap-table-next';
import classnames from 'classnames/bind';
import { ProductListOverview } from './ProductListOverview';
import { ImportProducts } from 'components/Rsuite/ImportFile';
import { RsButton } from 'components/Rsuite/Button';
import { Download } from 'react-feather';
import RsTooltip from 'components/Rsuite/Tooltip/Tooltip';
import { SearchBar } from 'components/SearchBar';

const cx = classnames.bind(styles);

export type ProductListProps = {
  filterSet: ProductFilter;
  productList: Product[];
  allProducts?: Product[];
  pagination: Pagination;
  editActions?: {
    label: string;
    onClick: (
      selectedProducts: string[],
      cb?: () => void,
    ) => void | Promise<void>;
  }[];
  handleCloseModal?: () => void;
  onListChange?: TableChangeHandler<Product>;
  handleSearchString?: (searchString: string) => void;
  singleSelect?: boolean;
  limitSelect?: boolean;
  availableProducts?: number;
  showImportButton?: boolean;
};

export const ProductList: React.FC<ProductListProps> = ({
  filterSet,
  pagination,
  productList,
  editActions,
  singleSelect,
  limitSelect,
  availableProducts,
  showImportButton,
  allProducts = productList,
  onListChange,
  handleSearchString,
}) => {
  const {
    state,
    columns,
    selectedProducts,
    preprocessProducts,
    // onProductListTypeChange,
    handleRemoveSelect,
    handleRemoveProduct,
  } = useProductListModel(productList, allProducts, singleSelect, limitSelect);

  const renderOverviewBar = () => {
    const renderItems = () => {
      if (!editActions) {
        return;
      }

      return editActions.map((editAction) => {
        const onClick = (event) => {
          event && event.stopPropagation();
          editAction.onClick(
            selectedProducts.map((p) => p.productId.toString()),
            handleRemoveSelect,
          );
        };
        return (
          <div key={editAction.label} onClick={onClick}>
            {i18n.t<string>(editAction.label)}
          </div>
        );
      });
    };
    const productToOverview = preprocessProducts();
    // const productToOverview = availbleProducts.filter((product) =>
    //   selectedProducts.includes(product.productId.toString()),
    // );

    return (
      <ProductListOverview
        products={productToOverview}
        handleRemoveAll={handleRemoveSelect}
        handleRemoveProduct={handleRemoveProduct}
      >
        {renderItems()}
      </ProductListOverview>
    );
  };

  const contentAreaClass = cx(styles.contentArea, {
    hasEditBar: preprocessProducts().length > 0,
  });

  return (
    <div className={styles.productListArea}>
      {preprocessProducts().length > 0 && (
        <div className={styles.productListTitleBar}>{renderOverviewBar()}</div>
      )}
      <div className={styles.importProducts}>
        <div className='flex items-center gap-2'>
          {showImportButton && (
            <>
              <ImportProducts
                retailId={filterSet.retailerId || ''}
                availableProducts={availableProducts || 25}
              />
              <RsButton
                className='!text-sm'
                appearance='subtle'
                onClick={() =>
                  window.open(
                    'https://docs.google.com/spreadsheets/d/1L9oHhYCmXI0Ki8f4ZdUWRPj1wixEyK8w/export?format=xlsx',
                    '_blank',
                    'noopener,noreferrer',
                  )
                }
              >
                <Download
                  width={20}
                  height={20}
                  className='mr-2 text-[#414651]'
                />
                <span className='mr-2 inline-block text-sm'>範例格式下載</span>
                <RsTooltip tooltipTrigger='hover' placement='right'>
                  <span className='inline-block whitespace-nowrap text-sm'>
                    最多上傳25筆，若任一商品ID有誤將匯入失敗
                  </span>
                </RsTooltip>
              </RsButton>
            </>
          )}
        </div>
        <SearchBar
          disabled={filterSet.advertiser === undefined}
          model={{
            placeholder: i18n.t<string>('productHome.placeholders.searchbar'),
            search: handleSearchString || (() => {}),
            defaultValue: filterSet.search,
          }}
        />
      </div>
      <div className={contentAreaClass}>
        <StickableBootstrapTable
          key={state.selectedProductListType}
          stickFirstColumn
          scrollByElementControl={false}
          remote={onListChange ? true : false}
          keyField={ProductListColumns.ID}
          data={productList}
          columns={columns}
          noDataIndication={
            filterSet.advertiser
              ? i18n.t<string>('productList.labels.noData')
              : i18n.t<string>('productList.labels.needAdvertiser')
          }
          defaultSorted={[
            {
              dataField: ProductListColumns.ID,
              order: 'desc',
            },
          ]}
          pagination={{
            page: pagination.page,
            sizePerPage: 25,
            totalSize: pagination.totalCount,
          }}
          onTableChange={onListChange}
        />
      </div>
    </div>
  );
};
